export default class MetaEmbeddedSignup {
    readonly configId: string;

    constructor(params: { configId: string }) {
        this.configId = params.configId;
    }

    async login(): Promise<MetaEmbeddedSignupResult> {
        return new Promise((resolve, reject) => {
            let sessionResult: { value: MetaEmbeddedSignupSessionResult } | { error: Error } | null;

            const session = new MetaEmbeddedSignupSession();
            session
                .first()
                .then((value) => (sessionResult = { value }))
                .catch((error) => (sessionResult = { error }));

            window.FB.login(
                (response: any) => {
                    const code = response.authResponse?.code;
                    if (typeof code === 'string') {
                        resolve({
                            code,
                            session: sessionResult && 'value' in sessionResult ? sessionResult.value : null,
                        });
                    } else {
                        reject(
                            new MetaEmbeddedSignupError({
                                session: sessionResult && 'error' in sessionResult ? sessionResult.error : null,
                            }),
                        );
                    }

                    session.dispose();
                },
                {
                    config_id: this.configId,
                    response_type: 'code',
                    override_default_response_type: true,
                    extras: {
                        // this `featureType` is necessary in order to enable Coexistence
                        featureType: 'whatsapp_business_app_onboarding',
                        // `sessionInfoVersion` is necessary in order to get events in `message`
                        sessionInfoVersion: '3',
                    },
                },
            );
        });
    }
}

export interface MetaEmbeddedSignupResult {
    code: string;
    session: MetaEmbeddedSignupSessionResult | null;
}

export class MetaEmbeddedSignupError extends Error {
    readonly session: Error | null;

    constructor(params: { session: Error | null }) {
        super();
        this.session = params.session;
    }

    get metaEvent(): Record<string, unknown> | null {
        return this.session instanceof MetaEmbeddedSignupSessionError ? this.session.event : null;
    }
}

export class MetaEmbeddedSignupSession {
    private _pending: {
        resolve: (result: MetaEmbeddedSignupSessionResult) => void;
        reject: (error: Error) => void;
    } | null = null;

    async first(): Promise<MetaEmbeddedSignupSessionResult> {
        if (this._pending !== null) throw new Error('Already waiting for a result.');

        return new Promise((resolve, reject) => {
            this._pending = { resolve, reject };
            window.addEventListener('message', this._messageListener);
        });
    }

    dispose() {
        window.removeEventListener('message', this._messageListener);
        this._pending = null;
    }

    private _messageListener = (event: MessageEvent) => {
        if (!event.origin.endsWith('facebook.com')) return;
        let messageData: any;
        try {
            messageData = JSON.parse(event.data);
        } catch {
            return;
        }
        if (messageData.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (!this._pending) {
            console.warn('Received session event without being waiting for it.');
            return;
        }

        const data = messageData.data;

        switch (messageData.event) {
            case 'FINISH':
            case 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING': {
                const result: MetaEmbeddedSignupSessionResult = {
                    businessAccountId: data.waba_id,
                };
                const phoneNumberId = data.phone_number_id;
                if (typeof phoneNumberId === 'string') {
                    result.phoneNumberId = phoneNumberId;
                }
                this._pending.resolve(result);
                break;
            }
            default:
                this._pending.reject(new MetaEmbeddedSignupSessionError({ event: messageData }));
                break;
        }

        this.dispose();
    };
}

export interface MetaEmbeddedSignupSessionResult {
    phoneNumberId?: string;
    businessAccountId: string;
}

// The session's event as Meta sent it: a CANCEL carries either the step the owner left at or Meta's error.
export class MetaEmbeddedSignupSessionError extends Error {
    readonly event: Record<string, unknown>;

    constructor(params: { event: Record<string, unknown> }) {
        super();
        this.event = params.event;
    }
}

declare global {
    interface Window {
        FB: any;
    }
}
